import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import api from '../utils/api';

const useBackgroundCompilation = () => {
  const [notifications, setNotifications] = useState([]);
  const [compilationJobs, setCompilationJobs] = useState({});
  const [failedSegments, setFailedSegments] = useState(new Set());
  const jobTimeouts = useRef({});
  const compilationJobsRef = useRef({});

  // Keep ref in sync with state
  useEffect(() => {
    compilationJobsRef.current = compilationJobs;
  }, [compilationJobs]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(jobTimeouts.current).forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  const addNotification = useCallback((notification) => {
    const id = notification.id || uuidv4();
    setNotifications(prev => [...prev, { ...notification, id }]);
    
    // Don't auto-remove here, we'll do it when updating to success/error
    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (jobTimeouts.current[id]) {
      clearTimeout(jobTimeouts.current[id]);
      delete jobTimeouts.current[id];
    }
  }, []);

  const updateNotification = useCallback((id, updates) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, ...updates } : n)
    );
    
    // Auto-remove after 2 seconds when changing to success or error
    if (updates.type === 'success' || updates.type === 'error') {
      // Clear any existing timeout
      if (jobTimeouts.current[id]) {
        clearTimeout(jobTimeouts.current[id]);
      }
      
      const timeout = setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
        delete jobTimeouts.current[id];
      }, 2000);
      jobTimeouts.current[id] = timeout;
    }
  }, []);

  const startCompilationJob = useCallback((jobId, questionInfo, segmentId) => {
    setCompilationJobs(prev => ({
      ...prev,
      [jobId]: { 
        status: 'processing', 
        questionInfo, 
        segmentId,
        startTime: Date.now() 
      }
    }));

    const notificationId = addNotification({
      id: jobId,
      type: 'processing',
      title: 'Locking Question',
      message: '',
      questionInfo,
      segmentId
    });

    return notificationId;
  }, [addNotification]);

  const updateCompilationProgress = useCallback((jobId, progress) => {
    setCompilationJobs(prev => ({
      ...prev,
      [jobId]: { ...prev[jobId], progress }
    }));

    updateNotification(jobId, { progress });
  }, [updateNotification]);

  const completeCompilationJob = useCallback((jobId, success, message, data = {}) => {
    const job = compilationJobsRef.current[jobId];
    
    if (!job) {
      console.warn('Job not found for completion:', jobId);
      return;
    }

    setCompilationJobs(prev => {
      const updated = { ...prev };
      delete updated[jobId];
      return updated;
    });

    if (success) {
      setFailedSegments(prev => {
        const updated = new Set(prev);
        updated.delete(job.segmentId);
        return updated;
      });

      updateNotification(jobId, {
        type: 'success',
        title: 'Success',
        message: ''
      });
    } else {
      setFailedSegments(prev => new Set(prev).add(job.segmentId));

      updateNotification(jobId, {
        type: 'error',
        title: 'Error',
        message: ''
      });
    }
  }, [updateNotification]);

  const lockQuestionInBackground = useCallback(async (
    currentQuestion,
    reviewData,
    onSuccess,
    onError
  ) => {
    const jobId = uuidv4();
    const questionInfo = {
      number: currentQuestion.question_number,
      part: currentQuestion.part_letter
    };

    // Start the job and show notification
    startCompilationJob(jobId, questionInfo, currentQuestion.segment_id);

    try {
      // Prepare edited content for compilation test
      const templated = currentQuestion.templated;
      let editedContent = {};
      
      if (templated.type === 'mcq') {
        editedContent = {
          question_content: reviewData.template_review?.template_data?.human_question_content || templated.question_content,
          option_a: reviewData.template_review?.template_data?.human_option_a || templated.option_a,
          option_b: reviewData.template_review?.template_data?.human_option_b || templated.option_b,
          option_c: reviewData.template_review?.template_data?.human_option_c || templated.option_c,
          option_d: reviewData.template_review?.template_data?.human_option_d || templated.option_d
        };
      } else {
        editedContent = {
          question_content: reviewData.template_review?.template_data?.human_main_content || templated.question_content,
          solution: reviewData.template_review?.template_data?.human_solution || templated.solution
        };
      }

      // Test compilation
      const compileResponse = await api.post('/review/templated/compile-test', {
        content_type: templated.type,
        content_id: currentQuestion.segment_id,
        edited_content: editedContent
      });
      
      const compileResult = compileResponse.data;
      
      // If compilation fails, complete with error
      if (!compileResult.success) {
        const errorMessage = compileResult.errors && compileResult.errors.length > 0 
          ? compileResult.errors.join('; ') 
          : 'Compilation failed';
        
        completeCompilationJob(jobId, false, 'Compilation failed');
        if (onError) onError(errorMessage, compileResult.errors);
        return;
      }

      // Compilation successful, call lock endpoint
      const lockResponse = await api.post(
        `/review/templated/lock-into-database/${currentQuestion.segment_id}`, 
        reviewData
      );
      
      const lockData = lockResponse.data;

      if (lockData.success) {
        completeCompilationJob(
          jobId, 
          true, 
          'Locked successfully'
        );
        if (onSuccess) onSuccess(lockData);
      } else {
        completeCompilationJob(
          jobId, 
          false, 
          lockData.message || 'Failed to lock'
        );
        if (onError) onError(lockData.message);
      }
    } catch (error) {
      console.error('Background compilation error:', error);
      const errorMessage = error.response?.data?.detail || 'Unknown error occurred';
      completeCompilationJob(jobId, false, errorMessage);
      if (onError) onError(errorMessage);
    }
  }, [startCompilationJob, completeCompilationJob]);

  return {
    notifications,
    failedSegments,
    addNotification,
    removeNotification,
    lockQuestionInBackground,
    isProcessing: Object.keys(compilationJobs).length > 0
  };
};

export default useBackgroundCompilation;